import React, { FC, type JSX } from 'react';

import { Icon } from '@iconify/react';
import { Typography } from '@mui/material';
import { TFunction } from 'i18next';
import { Trans, useTranslation } from 'react-i18next';

import { getDetectorWordsBalance } from '~/modules/tools/views/AiDetectorV4/helpers';
import { getHumanizerWordsBalance } from '~/modules/tools/views/AiHumanizerV4/helpers';
import { getPlagiarismWordsBalance } from '~/modules/tools/views/PlagiarismCheckerV4/helpers';
import { useAppSelector } from '~/store/hooks/useAppSelector';
import { Typographies } from '~/theme/typography';
import { REGULARITY } from '~/types/product';

import styles from './styles.module.scss';

const getDynamicBenefits = (data: {
  t: TFunction;
  plagiarismWordsAmount: string;
  detectorWordsAmount: string;
  humanizerWordsAmount: string;
}): JSX.Element[] => {
  const {
    t,
    plagiarismWordsAmount,
    detectorWordsAmount,
    humanizerWordsAmount,
  } = data;

  const detectors: { amount: string; tool_title: string }[] = [
    {
      amount: plagiarismWordsAmount,
      tool_title: t('general:check_plagiarism', 'Check Plagiarism'),
    },
    {
      amount: detectorWordsAmount,
      tool_title: t('general:ai_content_detector', 'AI Content Detector'),
    },
    {
      amount: humanizerWordsAmount,
      tool_title: t('general:ai_text_humanizer', 'AI Text Humanizer'),
    },
  ];

  return detectors.map((el, idx) => {
    return (
      <div key={`d${idx}`}>
        <Trans
          i18nKey="benefits[0]"
          t={t}
          values={{
            words_amount: el.amount,
            tool_title: el.tool_title,
          }}
        >
          <span>{'{{words_amount}}'} Words</span> for {'{{tool_title}}'}
        </Trans>
      </div>
    );
  });
};

const getStaticBenefits = (t: TFunction): JSX.Element[] => {
  return [
    <div key="s1">
      <Trans i18nKey="benefits[1]" t={t}>
        <span>Extra Long</span> Context AI Models
      </Trans>
    </div>,
    <div key="s2">
      <Trans i18nKey="benefits[2]" t={t}>
        <span>Lifetime</span> Browser Extension
      </Trans>
    </div>,
  ];
};

export enum BenefitsPack {
  DYNAMIC = 'dynamic',
  STATIC = 'static',
  BOTH = 'both',
}

type Props = {
  pack?: BenefitsPack;
};

const Benefits: FC<Props> = ({ pack = BenefitsPack.BOTH }) => {
  const { t } = useTranslation('general');
  const profile = useAppSelector((state) => state.profile);

  const getWordsAmount = (fallback: string | number): string => {
    try {
      const lang = profile?.platform_lang || 'en';
      const numberFormat = new Intl.NumberFormat(lang).format(Number(fallback));

      const isYearly = profile?.user_product?.regularity === REGULARITY.YEAR;

      return isYearly ? t?.('general:unlimited', 'Unlimited') : numberFormat;
    } catch (e) {
      return String(fallback);
    }
  };

  const dynamicBenefitsData = {
    t,
    plagiarismWordsAmount: getWordsAmount(getPlagiarismWordsBalance()),
    humanizerWordsAmount: getWordsAmount(getHumanizerWordsBalance()),
    detectorWordsAmount: getWordsAmount(getDetectorWordsBalance()),
  };

  return (
    <div className={styles.benefits}>
      {[
        ...(pack === BenefitsPack.DYNAMIC || pack === BenefitsPack.BOTH
          ? getDynamicBenefits(dynamicBenefitsData)
          : []),
        ...(pack === BenefitsPack.STATIC || pack === BenefitsPack.BOTH
          ? getStaticBenefits(t)
          : []),
      ].map((benefit, idx) => {
        return (
          <Typography
            key={idx}
            variant={Typographies.TITLE_MEDIUM}
            component="div"
            className={styles.benefits__item}
          >
            <Icon
              icon="material-symbols:check-rounded"
              className={styles.benefits__item_icon}
            />
            <div className={styles.benefits__item_text}>{benefit}</div>
          </Typography>
        );
      })}
    </div>
  );
};

export default Benefits;
